import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Printer, CheckCircle2, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Read-only driver contract presenter. Renders the admin-configured contract
 * markdown, lets the user print it, and — once explicitly accepted — calls
 * onAccept({ version }). Used during driver onboarding.
 */
export default function DriverContractDialog({ open, onOpenChange, onAccept, driverName }) {
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [version, setVersion] = useState(1);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/admin/policies');
        if (cancelled) return;
        setText(data?.driver_contract_markdown || '');
        setVersion(Number(data?.driver_contract_version) || 1);
      } catch (e) {
        try {
          const { data } = await api.get('/policies/public');
          if (cancelled) return;
          setVersion(Number(data?.driver_contract_version) || 1);
        } catch (_) { /* ignore */ }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const reset = () => { setAgreed(false); };
  const close = (next) => { if (!next) reset(); onOpenChange?.(next); };

  const accept = () => {
    if (!agreed) { toast.error('Tick the checkbox to accept the contract'); return; }
    onAccept?.({ version });
    close(false);
  };

  const printContract = () => {
    const w = window.open('', '_blank');
    if (!w) { toast.error('Pop-up blocked — enable pop-ups to print'); return; }
    w.document.write(`<!doctype html><html><head><title>Driver Contract v${version}</title>
      <style>
        body { font-family: 'Space Grotesk', system-ui, sans-serif; padding: 32px; max-width: 720px; margin: 0 auto; color: #22262A; }
        h1, h2, h3 { font-family: 'Spectral', serif; }
        h1 { font-size: 28px; margin-bottom: 4px; }
        pre { white-space: pre-wrap; word-wrap: break-word; font-family: inherit; line-height: 1.55; }
        .meta { color: #6b6f73; font-size: 12px; margin-bottom: 24px; }
        .sig { margin-top: 48px; border-top: 1px solid #d4d4d8; padding-top: 16px; font-size: 12px; color: #6b6f73; }
      </style>
    </head><body>
      <div class="meta">City Grill · Driver Contract v${version} · ${new Date().toLocaleString()}${driverName ? ` · ${driverName}` : ''}</div>
      <pre>${(text || '').replace(/[<>&]/g, (m) => ({'<':'&lt;','>':'&gt;','&':'&amp;'})[m])}</pre>
      <div class="sig">Signature: ____________________________ &nbsp;&nbsp; Date: __________</div>
    </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="driver-contract-dialog" className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" /> Driver agreement (v{version})
          </DialogTitle>
          <DialogDescription>
            {driverName ? `${driverName}, please read the contract carefully.` : 'Please read the contract carefully before accepting.'}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <ScrollArea className="max-h-[55vh] rounded-md border border-border bg-secondary/30 p-4">
            <pre data-testid="driver-contract-body" className="whitespace-pre-wrap break-words font-sans text-sm leading-6 text-foreground">{text || 'No contract has been configured yet. Ask an administrator to set one in Admin → Policies & Settings.'}</pre>
          </ScrollArea>
        )}
        <div className="flex items-start gap-2 rounded-md border border-border bg-card p-2 text-sm">
          <Checkbox data-testid="driver-contract-agree" id="driver-contract-agree" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} />
          <label htmlFor="driver-contract-agree" className="cursor-pointer leading-snug">
            I have read and accept the City Grill driver agreement (v{version}).
          </label>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => close(false)}>Cancel</Button>
          <Button data-testid="driver-contract-print" variant="secondary" onClick={printContract}><Printer className="mr-1 h-4 w-4" /> Print</Button>
          <Button data-testid="driver-contract-accept" onClick={accept} disabled={!agreed || loading}>
            <CheckCircle2 className="mr-1 h-4 w-4" /> Accept & continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
